<template>
    <el-dialog :visible.sync="visible"
               :title="$t('emailVerification.title')"
               width="480px"
               custom-class="email-verification-dialog"
               :close-on-click-modal="false"
               :close-on-press-escape="!submitting"
               :show-close="!submitting"
               append-to-body
               @close="closeDialog">
        <div class="email-verification-content">
            <div class="email-verification-icon">
                <i class="el-icon-message"></i>
            </div>
            <p class="email-verification-description">
                {{$t('emailVerification.description')}}
            </p>
            <p class="email-verification-email">{{email}}</p>
            <el-input v-model="code"
                      class="email-verification-code"
                      :placeholder="$t('emailVerification.codePlaceholder')"
                      maxlength="6"
                      autocomplete="one-time-code"
                      @input="normalizeCode"
                      @keyup.enter.native="verifyEmail"></el-input>
            <p v-if="statusMessage" class="email-verification-status" :class="statusType">
                {{statusMessage}}
            </p>
        </div>
        <div slot="footer" class="email-verification-footer">
            <button type="button" class="email-verification-contact" @click="contactUs">
                {{$t('emailVerification.contactUs')}}
            </button>
            <div class="email-verification-actions">
                <el-button :loading="resending"
                           :disabled="remainingSendCount === 0 || submitting || resendCountdown > 0"
                           @click="resendCode">
                    {{resendButtonText}}
                </el-button>
                <el-button type="primary"
                           :loading="submitting"
                           :disabled="code.length === 0 || resending"
                           @click="verifyEmail">
                    {{$t('emailVerification.verify')}}
                </el-button>
            </div>
        </div>
    </el-dialog>
</template>

<script>
    import bus, {finishEmailVerification} from '@/utils/emailVerificationDialog'

    export default {
        name: 'EmailVerificationDialog',
        data() {
            return {
                visible: false,
                email: '',
                code: '',
                remainingSendCount: 0,
                submitting: false,
                resending: false,
                resendCountdown: 0,
                resendTimer: null,
                statusMessage: '',
                statusType: ''
            }
        },
        computed: {
            resendButtonText() {
                if (this.resendCountdown > 0) {
                    return `${this.$t('emailVerification.resend')} (${this.resendCountdown}s)`
                }
                return this.$t('emailVerification.resend')
            }
        },
        methods: {
            openDialog(challenge) {
                this.email = challenge.email || ''
                this.remainingSendCount = Number(challenge.remainingSendCount || 0)
                this.code = ''
                if (!challenge.codeAvailable && this.remainingSendCount === 0) {
                    this.statusMessage = this.$t('emailVerification.limitReached')
                    this.statusType = 'error'
                } else {
                    this.statusMessage = challenge.codeSent
                        ? this.$t('emailVerification.sent')
                        : this.$t('emailVerification.enterCode')
                    this.statusType = challenge.codeSent ? 'success' : ''
                }
                this.visible = true
            },
            normalizeCode(value) {
                this.code = String(value || '').slice(0, 6)
                this.statusMessage = ''
                this.statusType = ''
            },
            async verifyEmail() {
                if (this.submitting || this.code.length === 0) {
                    return
                }
                this.submitting = true
                try {
                    const response = await this.$axios.post('verify_login_email', {
                        params: {code: this.code}
                    })
                    if (response.data.errorCode === 0) {
                        this.statusMessage = this.$t('emailVerification.success')
                        this.statusType = 'success'
                        this.$message.success(this.$t('emailVerification.success'))
                        this.visible = false
                        finishEmailVerification(true)
                    } else {
                        this.statusMessage = this.$t('emailVerification.failed')
                        this.statusType = 'error'
                    }
                } finally {
                    this.submitting = false
                }
            },
            async resendCode() {
                if (this.resending || this.remainingSendCount === 0 || this.resendCountdown > 0) {
                    return
                }
                this.resending = true
                try {
                    const response = await this.$axios.post('send_login_email_verification_code', {
                        params: {}
                    })
                    const challenge = response.data.objectData || {}
                    this.remainingSendCount = Number(challenge.remainingSendCount || 0)
                    if (response.data.errorCode === 0) {
                        this.code = ''
                        this.statusMessage = this.$t('emailVerification.resent')
                        this.statusType = 'success'
                        this.startResendCountdown()
                    } else {
                        this.statusMessage = this.$t('emailVerification.limitReached')
                        this.statusType = 'error'
                    }
                } finally {
                    this.resending = false
                }
            },
            startResendCountdown() {
                if (this.resendTimer != null) {
                    clearInterval(this.resendTimer)
                }
                this.resendCountdown = 60
                this.resendTimer = setInterval(() => {
                    this.resendCountdown--
                    if (this.resendCountdown <= 0) {
                        clearInterval(this.resendTimer)
                        this.resendTimer = null
                        this.resendCountdown = 0
                    }
                }, 1000)
            },
            closeDialog() {
                if (!this.visible) {
                    finishEmailVerification(false)
                }
            },
            contactUs() {
                window.location.href = 'mailto:goodinvoicego@gmail.com'
            }
        },
        mounted() {
            bus.$on('open', this.openDialog)
        },
        beforeDestroy() {
            bus.$off('open', this.openDialog)
            if (this.resendTimer != null) {
                clearInterval(this.resendTimer)
            }
        }
    }
</script>

<style>
    .email-verification-dialog {
        border-radius: var(--ui-radius-panel);
        overflow: hidden;
    }

    .email-verification-dialog .el-dialog__header {
        padding: 28px 32px 16px;
    }

    .email-verification-dialog .el-dialog__title {
        color: #111c35;
        font-size: var(--ui-font-section-title);
        font-weight: 600;
    }

    .email-verification-dialog .el-dialog__body {
        padding: 10px 32px 18px;
    }

    .email-verification-dialog .el-dialog__footer {
        padding: 0 32px 28px;
    }

    .email-verification-content {
        text-align: center;
    }

    .email-verification-icon {
        width: 64px;
        height: 64px;
        margin: 0 auto 18px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        color: #1f7af2;
        background: #eaf3ff;
        font-size: 30px;
    }

    .email-verification-description {
        color: var(--ui-text-muted);
        line-height: 1.55;
    }

    .email-verification-description {
        margin: 0;
    }

    .email-verification-email {
        margin: 6px 0 22px;
        color: #111c35;
        font-weight: 600;
        word-break: break-all;
    }

    .email-verification-code input {
        height: 52px;
        border-radius: var(--ui-radius-control);
        text-align: center;
        font-size: 16px;
        letter-spacing: normal;
    }

    .email-verification-status {
        min-height: 22px;
        margin: 10px 0 0;
        color: var(--ui-text-muted);
    }

    .email-verification-status.success {
        color: #1da861;
    }

    .email-verification-status.error {
        color: #f05b61;
    }

    .email-verification-footer {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
    }

    .email-verification-contact {
        padding: 0;
        border: 0;
        color: #1f7af2;
        background: transparent;
        cursor: pointer;
        font-size: 14px;
    }

    .email-verification-actions {
        display: flex;
        gap: 12px;
    }

    .email-verification-actions .el-button {
        min-width: 112px;
        height: var(--ui-toolbar-height);
        border-radius: var(--ui-radius-control);
    }

    @media (max-width: 560px) {
        .email-verification-dialog {
            width: calc(100% - 32px) !important;
        }

        .email-verification-dialog .el-dialog__header,
        .email-verification-dialog .el-dialog__body,
        .email-verification-dialog .el-dialog__footer {
            padding-left: 16px;
            padding-right: 16px;
        }

        .email-verification-footer,
        .email-verification-actions {
            width: 100%;
        }

        .email-verification-actions .el-button {
            flex: 1;
        }
    }
</style>
